/**
 * Ephemeral-environment ("mini-me") host derivation.
 *
 * WHY THIS EXISTS
 *
 * Every ephemeral environment is a separate namespace fronted by one shared nginx
 * terminator that routes on hostname:
 *
 *     wallet-93f746fc.mini-me.dev      -> account-frontend  in ns mini-me-93f746fc
 *     wallet-api-93f746fc.mini-me.dev  -> account-api       in ns mini-me-93f746fc
 *     api-93f746fc.mini-me.dev         -> idp-web           in ns mini-me-93f746fc
 *     enroll-93f746fc.mini-me.dev      -> IPA               in ns mini-me-93f746fc
 *
 * The 8-hex suffix IS the environment identity. Before this module, the app fell
 * through `getReactEnv()` to the `development` case and used HARDCODED FLAT names
 * (`https://wallet-api.idme.test/api`). Those flat names resolve, through the shared
 * terminator's legacy block, to ONE designated primary namespace -- so a tester on
 * environment B had their API calls silently served by environment A. Wrong data, no
 * error. That is the bug this file removes.
 *
 * WHY DERIVE RATHER THAN INJECT AT BUILD TIME
 *
 * Deriving from window.location.hostname means ONE image serves EVERY environment.
 * Baking the value in (NEXT_PUBLIC_*-style, or any build arg) would require an image
 * build per environment, which is disqualifying for self-serve provisioning -- the
 * whole point is that creating an environment costs no control-plane work. A runtime
 * /config.json fetch would also work; this needs no serving-side machinery at all.
 *
 * SCOPE: activates ONLY on the two ephemeral domains. Every existing environment
 * (id.me, idme.cc, idmelabs.com, ...) is untouched and takes the original code path.
 */

// The domains ephemeral environments are served on. `idme.test` is the local/legacy
// name; `mini-me.dev` is the cloud name and is where these environments actually live.
// Both are listed because the shared terminator serves both today -- `.test` works
// with the self-signed cert, `.dev` is HSTS-preloaded and needs the real one.
export const EE_DOMAINS = ['mini-me.dev', 'idme.test'];

// <service>[-<8 hex>].<ee domain>
//   group 1 = service label      (wallet, wallet-api, api, enroll, account, ...)
//   group 2 = environment hash   (undefined for the legacy flat form)
//   group 3 = ee domain
const EE_HOST = new RegExp(
  `^([a-z0-9-]+?)(?:-([0-9a-f]{8}))?\\.(${EE_DOMAINS.map((d) =>
    d.replace(/\./g, '\\.'),
  ).join('|')})$`,
);

/**
 * Parse an ephemeral hostname. Returns null for anything that is not one, which is
 * the signal for callers to fall back to the pre-existing environment switch.
 *
 * `hash` is null for the legacy flat form (`wallet.idme.test`) so that the derived
 * siblings stay flat too -- mixing a flat page with hashed API calls would cross
 * environments, which is the exact bug being fixed.
 */
export const parseEphemeralHost = (hostname) => {
  const m = EE_HOST.exec(hostname || '');
  if (!m) return null;
  return { service: m[1], hash: m[2] || null, domain: m[3] };
};

/** Build a sibling service's host in the SAME environment. */
export const ephemeralHostFor = (service, { hash, domain }) =>
  `${service}${hash ? `-${hash}` : ''}.${domain}`;

/** Build a sibling service's origin in the SAME environment. */
export const ephemeralOriginFor = (service, parsed) =>
  `https://${ephemeralHostFor(service, parsed)}`;

/**
 * The ephemeral environment for the CURRENT page, or null when not in one.
 * Guarded for non-browser contexts (SSR, tests, jsdom without a location).
 */
export const currentEphemeralEnv = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  return parseEphemeralHost(window.location.hostname);
};
